/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

import { ConceptXFacts } from './vendor/conceptx';
import { PermutiveFacts } from './vendor/permutive';
import { WeboramaFacts } from './vendor/weborama';

import type { RtdModuleConfig } from '../../types/prebid';
import type { Facts } from '../index';

class GlobalFactStore {
  private static instance: GlobalFactStore;
  private globalFacts: Facts = {};
  private fixedClientFacts: Facts = {};
  private runtimeClientFacts: Facts = {};
  private vendorFacts: Facts = {};
  private enabledCids: string[] | null = null;
  private configData: RtdModuleConfig | null = null;

  private constructor() {}

  public static getInstance(): GlobalFactStore {
    if (!GlobalFactStore.instance) {
      GlobalFactStore.instance = new GlobalFactStore();
    }
    return GlobalFactStore.instance;
  }

  public update(globalFacts: Facts = {}): void {
    this.globalFacts = { ...globalFacts };
    if (globalFacts['page.url']) {
      try {
        this.globalFacts['page.path'] = new URL(globalFacts['page.url']).pathname;
      } catch (error) {}
    }
  }

  public setEnabledCids(enabledCids: string[]): void {
    this.enabledCids = enabledCids;
  }

  public setConfig(config: RtdModuleConfig): void {
    this.configData = config;
  }

  public getFacts(): Facts {
    if (Object.keys(this.fixedClientFacts).length === 0) {
      this.setFixedClientFacts();
    }
    this.updateVendorFacts();
    this.updateRuntimeFacts();
    return {
      ...this.globalFacts,
      ...this.fixedClientFacts,
      ...this.runtimeClientFacts,
      ...this.vendorFacts,
    };
  }

  private updateRuntimeFacts() {
    this.runtimeClientFacts = {
      'browser.width': Math.round(window?.visualViewport?.width ?? 0),
      'browser.height': Math.round(window?.visualViewport?.height ?? 0),
      'time.day': new Date().getDay(),
      'time.hour': new Date().getHours(),
      'time.minutes': new Date().getMinutes(),
      'page.text': (document?.body?.innerText ?? '').toLowerCase(),
    };
  }

  private setFixedClientFacts(): void {
    const keywordsMetaTag: Element | null = document.querySelector('meta[name="keywords"]');
    this.fixedClientFacts = {
      'browser.lang': navigator?.language.toLowerCase(),
      'time.offset': new Date().getTimezoneOffset() / 60,
      'page.title': document?.title,
      'page.keywords': keywordsMetaTag?.getAttribute('content') ?? '',
    };
  }

  private updateVendorFacts(): void {
    if (!this.configData) {
      this.vendorFacts = {
        'publisher.cohort.ids': [],
      };
      return;
    }

    const detectedCohortIds = [
      ...PermutiveFacts.get(this.configData.params?.ptr?.permutive ?? {}),
      ...WeboramaFacts.get(this.configData.params?.ptr?.weborama ?? {}),
      ...ConceptXFacts.get(this.configData.params?.ptr?.conceptx ?? {}),
    ].filter((item) => !this.enabledCids || this.enabledCids?.includes(item));
    this.vendorFacts = {
      'publisher.cohort.ids': detectedCohortIds,
    };
  }

  // This method is used for testing purposes only
  public _reset(): void {
    this.globalFacts = {};
    this.fixedClientFacts = {};
    this.runtimeClientFacts = {};
    this.vendorFacts = {};
    this.enabledCids = null;
    this.configData = null;
  }
}

export { GlobalFactStore };
